'use client';
import { useId, useState, type FormEvent } from 'react';
import { Input } from 'components/UI/Input';
import styles from './love-oracle.module.scss';

function getLoveOracleResultText(percentage: number): string {
  const offsetFromMinimum = percentage - 50;
  const division = Math.ceil(offsetFromMinimum / 10);

  const resultTexts = [
    'Das passt nicht so richtig! Es könnte zwar schlimmer sein, aber eine schöne Beziehung oder die große Liebe wird es leider nicht. Es gibt durchaus passendere Partner für euch!',
    'Da ist doch mehr drin, findet ihr nicht? Zwar harmoniert ihr einigermaßen und würdet eine Beziehung durchaus hinbekommen. Aber die große Liebe ist es einfach nicht!',
    'Ja, das Ergebnis ist durchaus befriedigend. Versuchen könnt ihr es ja, wenn euch danach ist – und wer weiß, vielleicht hält das sogar?',
    'Man muss schon sagen: Ihr dürft euch glücklich schätzen, dass ihr so jemanden gefunden habt! Egal, ob ihr schon ein Paar seid oder eine Beziehung bloß euer sehnlichster Wunsch – ihr beide stellt eine solide Basis für eine lange Beziehung dar.',
    'Das passt perfekt! Ihr habt euren Herzensmenschen, eure große Liebe gefunden. Das gelingt nur selten im Leben – haltet euer Glück fest!',
  ];

  const index = Math.max(0, Math.min(4, division - 1));
  return resultTexts[index];
}

function getAsciiValue(name: string): number {
  let weight = 1;
  let sum = 0;
  const value = name.toLowerCase();
  const length = value.length;

  if (length > 0) {
    for (let index = 0; index < length; index++) {
      sum += value.charCodeAt(index) * weight;
      weight++;
    }
  }

  return sum;
}

function calculateLoveOracle(name1: string, name2: string, variance: number = 50): number {
  const asciiName1 = getAsciiValue(name1.trim());
  const asciiName2 = getAsciiValue(name2.trim());
  let nameVal = asciiName1 + asciiName2;
  const modulus = variance + 1;
  nameVal = nameVal % modulus;
  const loveMatch = 100 - variance + nameVal;

  return loveMatch;
}

export const WidgetToolsCalculatorLoveOracle = () => {
  const firstName1Id = useId();
  const lastName1Id = useId();
  const firstName2Id = useId();
  const lastName2Id = useId();

  const [firstName1, setFirstName1] = useState('');
  const [lastName1, setLastName1] = useState('');
  const [firstName2, setFirstName2] = useState('');
  const [lastName2, setLastName2] = useState('');
  const [result, setResult] = useState<number | null>(null);
  const [resultFirstName1, setResultFirstName1] = useState('');
  const [resultLastName1, setResultLastName1] = useState('');
  const [resultFirstName2, setResultFirstName2] = useState('');
  const [resultLastName2, setResultLastName2] = useState('');

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (firstName1 && lastName1 && firstName2 && lastName2) {
      const fullName1 = `${firstName1} ${lastName1}`;
      const fullName2 = `${firstName2} ${lastName2}`;

      const lovePercentage = calculateLoveOracle(fullName1, fullName2);

      setResult(lovePercentage);
      setResultFirstName1(firstName1);
      setResultLastName1(lastName1);
      setResultFirstName2(firstName2);
      setResultLastName2(lastName2);
    }
  }

  return (
    <div className={styles.root}>
      <form onSubmit={onSubmit}>
        <div className={styles.fieldWrapper}>
          <Input
            id={firstName1Id}
            label="Vorname"
            value={firstName1}
            onChange={(event) => {
              setFirstName1(event.currentTarget.value);
            }}
            required
          />
          <Input
            id={lastName1Id}
            label="Nachname"
            value={lastName1}
            onChange={(event) => {
              setLastName1(event.currentTarget.value);
            }}
            required
          />
        </div>

        <div className={styles.plus}>+</div>

        <div className={styles.fieldWrapper}>
          <Input
            id={firstName2Id}
            label="Vorname"
            value={firstName2}
            onChange={(event) => {
              setFirstName2(event.currentTarget.value);
            }}
            required
          />
          <Input
            id={lastName2Id}
            label="Nachname"
            value={lastName2}
            onChange={(event) => {
              setLastName2(event.currentTarget.value);
            }}
            required
          />
        </div>

        <button className={styles.button} type="submit">
          Auswerten
        </button>
      </form>

      {result !== null && (
        <div className={styles.result} aria-live="polite">
          <div className={styles.headline}>
            {resultFirstName1} {resultLastName1} und {resultFirstName2} {resultLastName2} passen zu {result}% zusammen
          </div>

          <div className={styles.resultText}>{getLoveOracleResultText(result)}</div>
        </div>
      )}
    </div>
  );
};
